/**
 * English catalogue.
 *
 * Keys are flat and dotted, grouped by the screen they appear on with a `common.*` block for the
 * words that genuinely repeat. Resist growing that block: "Save" is shared, but a hint that happens
 * to read the same on two screens is two strings that are free to diverge, and merging them means
 * a wording change on one screen silently rewrites the other.
 *
 * A plural entry is `{ one, other }` and is selected on `vars.n`. Everything else is a plain string
 * with `{name}` placeholders.
 */
export default {
  // ── Shared ────────────────────────────────────────────────────────────────────────────────────
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.close": "Close",
  "common.retry": "Try again",
  "common.loading": "Loading…",
  "common.confirm": "Confirm",
  "common.neverMind": "Never mind",

  "common.money": "€{amount}",

  // Every distance in the app goes through these two, so the abbreviation is decided in one place.
  "units.km": "{n} km",
  "units.m": "{n} m",
  "units.kg": "{n} kg",

  "offer.seatsOffered": "Seats offered",
  "offer.seatWord": { one: "seat", other: "seats" },
  "offer.seatsAlreadyTaken": { one: "1 seat is already taken", other: "{n} seats are already taken" },

  "past.drove": "DROVE",
  "past.rode": "RODE",
  "past.riders": { one: "1 rider", other: "{n} riders" },
  "past.withDriver": "with {name}",
  "past.driverWord": "driver",
  "past.free": "free",

  // ── Location permission ───────────────────────────────────────────────────────────────────────
  "geo.unavailable": "Your device can't get a location fix right now — this is common indoors or underground.",
  "geo.timeout": "Finding your location is taking longer than expected.",
  "geo.unsupported": "This browser can't share your location.",
  "geo.denied.orSearch": "Or just search for the address below.",
  "geo.denied.insecure":
    "This page isn't on a secure (HTTPS) connection, so the browser won't share your location and won't offer to ask.",
  "geo.denied.ios":
    "Location is off for this site. Tap “aA” in the address bar → Website Settings → Location → Ask, then try again. If that's already set, check Settings → Privacy & Security → Location Services → Safari Websites.",
  "geo.denied.android":
    "Location is blocked for this site. Tap the lock icon beside the address bar → Permissions → Location → Allow. If it's already allowed there, check Android Settings → Apps → your browser → Permissions → Location.",
  "geo.denied.desktop":
    "Location is blocked for this site. Allow it in your browser's site settings — the icon beside the address bar.",

  // ── Traffic verdicts ──────────────────────────────────────────────────────────────────────────
  "traffic.normal": "Moving freely",
  "traffic.slow": "Slow overall",
  "traffic.jam": "Heavy traffic",
  "traffic.unknown": "Traffic unknown",

  // ── Map ───────────────────────────────────────────────────────────────────────────────────────
  "map.boardHere": "BOARD HERE",
  "map.getOffHere": "GET OFF HERE",
  "map.boardAt": "Board at {place}",
  "map.getOffAt": "Get off at {place}",
  "map.towards": "towards {place}",
  "map.stopsAfterBoarding": { one: "1 stop after boarding", other: "{n} stops after boarding" },
  "map.departs": "Departs {time}",
  "map.arrives": "Arrives {time}",
  "map.unavailable": "Map unavailable",
  "map.noApiKey": "No Google Maps API key is configured. Set GATSBY_GOOGLE_MAPS_API_KEY and restart the dev server.",
  "map.loadFailed": "Google Maps failed to load — check the key's referrer restrictions and that the Maps JavaScript API is enabled.",

  // ── Past rides ────────────────────────────────────────────────────────────────────────────────
  "pastRides.title": "Your past rides",
  "pastRides.subtitle": "Everywhere you've been across the carpool — driver and passenger.",
  "pastRides.roleLabel": "ROLE",
  "pastRides.whenLabel": "WHEN",
  "pastRides.role.all": "All",
  "pastRides.role.drove": "Drove",
  "pastRides.role.rode": "Rode",
  "pastRides.when.7": "Last 7 days",
  "pastRides.when.30": "Last 30 days",
  "pastRides.when.all": "All time",
  "pastRides.empty.title": "No rides match these filters",
  "pastRides.empty.hint": "Try widening the time range or switching role.",
  "pastRides.count": { one: "1 ride", other: "{n} rides" },

  // ── Date & time picker ────────────────────────────────────────────────────────────────────────
  // Month and weekday names are NOT here — they come from Intl, which already has them right in
  // both languages including Greek's inflected forms. See DateTimeField.
  "dtf.placeholder": "Pick a date and time",
  "dtf.dialogLabel": "Choose a date and time",
  "dtf.prevMonth": "Previous month",
  "dtf.nextMonth": "Next month",
  "dtf.time": "Time",
  "dtf.earlier15": "15 minutes earlier",
  "dtf.later15": "15 minutes later",
  "dtf.done": "Done",

  // ── Pickup field ──────────────────────────────────────────────────────────────────────────────
  "pickup.clear": "Clear pickup",
  "pickup.searchAddress": "Search a starting address…",
  "pickup.changeAddress": "Change starting address…",
  "pickup.unsupported": "This browser can't share your location — search for the address instead.",
  "pickup.unavailable": "Your location isn't available right now — search for the address instead.",
  "pickup.timeout": "That took too long. Try again, or search for the address instead.",
  "pickup.failed": "Couldn't get your location — search for the address instead.",

  // ── Edit ride ─────────────────────────────────────────────────────────────────────────────────
  "edit.title": "Change this ride",
  "edit.titleReturn": "Change the trip home",
  "edit.everyoneTold": "Everyone who has a seat, or has asked for one, is told what changed.",
  "edit.returnPairedHint": "Changing this moves the trip home, which is a separate ride with its own riders. Clearing it cancels that ride.",
  "edit.returnNewHint": "Setting this posts a second ride going the other way, so colleagues can find a lift home.",
  "edit.until": "You can make changes until {when}.",
  "edit.departure": "Departure",
  "edit.return": "Return (optional)",
  "edit.noReturn": "No return trip",
  "edit.returnHintExisting":
    "Changing this moves the trip home, which is a separate ride with its own riders. Clearing it cancels that ride.",
  "edit.returnHintNew":
    "Setting this posts a second ride going the other way, so colleagues can find a lift home.",
  "edit.pickupPoint": "Pickup point",
  "edit.pickupNow": "Now: {place}",
  "edit.pickupHintMoved": "Moving the pickup re-routes the trip, so the fuel and toll share is recalculated.",
  "edit.pickupHintKeep": "Leave it alone to keep the current pickup point.",
  "edit.waysToGetThere": "{n} WAYS TO GET THERE",
  "edit.tolls": "€{amount} tolls",
  "edit.noTolls": "No tolls",
  "edit.routeHint": "Riders share the fuel and tolls for the road you pick, so this changes what they pay.",
  "edit.notes": "Notes for riders",
  "edit.notesPlaceholder": "Music ok, no pets…",
  "edit.notesHint": "Changing only the notes won't notify anyone.",
  "edit.saveChanges": "Save changes",

  // ── Brand ──────────────────────────────────────────────────────────────────────────────────────
  // Carried from the Greek rather than the other way round — see the note on the el.js entry.
  // "carpooling made easy" was replaced because "X made easy" describes the software instead of
  // saying anything about the ride.
  "brand.tagline": "the road wants company",

  // ── Offer a ride ──────────────────────────────────────────────────────────────────────────────
  "offer.subtitle": "Drop a trip on the feed — colleagues nearby will request to join.",
  "offer.stretch.slow": "one slow stretch",
  "offer.stretch.jam": "jammed in places",
  "offer.route.fuelEfficient": "Fuel efficient",
  "offer.route.fastest": "Fastest",
  "offer.route.alternative": "Alternative",
  "offer.err.noPickup.title": "No starting point yet",
  "offer.err.noPickup.body":
    "Search for an address, or use your current location — the pickup is what colleagues match against, so a ride cannot be posted without one.",
  "offer.err.noCar.title": "Your car isn't set up",
  "offer.err.noCar.body":
    "Everyone aboard splits the fuel, yourself included, so we need to know what your car actually uses before quoting anyone a price. It takes one tap and two numbers in Profile.",
  "offer.adjusted.title": "Posted, with the figures adjusted",
  "offer.adjusted.body":
    "The route you picked came back outside what the server measured for this journey, so it priced the ride from its own routing instead. Your passengers are charged these figures, not the preview.",
  "offer.seeTheRide": "See the ride",
  "offer.err.notDriver": "You're not set up to drive",
  "offer.err.postFailed": "Couldn't post this ride",
  "offer.err.serverRefused": "The server didn't accept the ride. Check the departure time and seats, then try again.",
  "offer.addCarFirst": "Add your car first",
  "offer.addCarBody":
    "Everyone aboard splits the fuel, yourself included, so we need to know what your car actually uses before quoting anyone a price. It takes one tap and two numbers.",
  "offer.setUpCar": "Set up my car",
  "offer.trafficAt": "traffic at {time}",
  "offer.seatsAvailable": "Seats available",
  "offer.alsoReturn": "Also post a ride home",
  "offer.returnDeparture": "Return departure",
  "offer.pickReturnTime": "Pick the return time",
  "offer.free.title": "Offer this ride for free",
  "offer.free.body": "Riders pay nothing — you cover the cost. You still earn points.",
  "offer.tolls": "Tolls",
  "offer.fromYourRoute": "from your route",
  "offer.avoidsTolls": "The route you picked avoids the tolls.",
  "offer.noTollRoads": "No toll roads on this route.",
  "offer.tollsNeedPickup": "Pick a starting point and we'll work the tolls out from the route.",
  "offer.notes": "Notes (optional)",
  "offer.notesPlaceholder": "Music ok, no smoking, etc.",
  "offer.needPickupTitle": "Search for a starting address first",
  "offer.pickStartFirst": "Pick a starting point first",
  "offer.postRide": "Post ride",
  "offer.preview.free": "Free for your riders",
  "offer.preview.split": "What it will cost",
  "offer.distance": "Distance",
  "offer.costToRiders": "Cost to riders",
  "offer.eachRiderPays": "EACH RIDER PAYS",
  "offer.eachPersonPays": "EACH PERSON PAYS",
  "offer.coveringTrip": "You're covering the trip — riders see a \"FREE\" badge.",
  "offer.fuel": "Fuel",
  "offer.fuelDetail": "Fuel · {consumption} {unit}/100km @ €{price}",
  "offer.totalTripCost": "Total trip cost",
  "offer.sharingTheCost": "Sharing the cost",
  "offer.youPlusRiders": { one: "{total} (you + 1 rider)", other: "{total} (you + {n} riders)" },
  // {profile} and {change} mark the links; the view splits on them.
  "offer.addCarHint": "Add what your car uses and what you pay per {unit} in {profile} and this fills itself in.",
  "offer.yourProfile": "your profile",
  "offer.fromProfile": "From your profile — {change} if that's not your car.",
  "offer.changeThem": "change them",
  // {alone} marks the emphasised single-rider price.
  "offer.ifAllFill": {
    one: "if the seat fills — {alone} each if just one colleague joins. You pay a share too, so it drops for everyone as more people come along.",
    other: "if all {n} seats fill — {alone} each if just one colleague joins. You pay a share too, so it drops for everyone as more people come along.",
  },
  "offer.pointsHint": "You'll earn {points} for every rider you carry.",
  "offer.pointsAmount": "+{n} points",

  // ── Ride detail ───────────────────────────────────────────────────────────────────────────────
  /*
   * Ride status, for the chip on the detail page.
   *
   * These duplicate `rideStatus.*` in the backend's messages.properties on purpose: the server
   * needs them to build "This ride is cancelled — it can no longer be changed", and the SPA needs
   * them for a chip the server is not involved in. Sentence-case here because the chip is a label;
   * the backend's are lower-case because they sit mid-sentence.
   */
  "rideStatus.OPEN": "Open",
  "rideStatus.FULL": "Full",
  "rideStatus.IN_PROGRESS": "In progress",
  "rideStatus.COMPLETED": "Completed",
  "rideStatus.CANCELLED": "Cancelled",

  "bookingStatus.PENDING": "Pending",
  "bookingStatus.CONFIRMED": "Confirmed",
  "bookingStatus.REJECTED": "Rejected",
  "bookingStatus.CANCELLED": "Cancelled",
  "bookingStatus.COMPLETED": "Completed",

  "detail.transitToPickup": "Transit to pickup",
  "detail.onFoot": "on foot",
  "detail.gettingToPickup": "GETTING TO THE PICKUP",
  "detail.from": "From",
  "detail.to": "To",
  "detail.viewArrow": "View →",
  "detail.complete": "Complete",
  "detail.alsoCancelReturn": "Also cancel the return trip home",
  "detail.byTransit": "by transit",
  "detail.line": "line {line}",
  "detail.showSteps": "Show steps",
  "detail.hideSteps": "Hide steps",
  "detail.takeLine": "Take {line}",
  "detail.takeTransit": "Take transit",
  "detail.fromStop": "from {stop}",
  "detail.toStop": "to {stop}",
  "detail.walkDistance": "Walk {distance}",
  "detail.stops": { one: "1 stop", other: "{n} stops" },
  // {place} marks the emphasised pickup name; the view splits on it.
  "detail.arriveAt": "Arrive at {place}",
  "detail.thePickupPoint": "the pickup point",

  "detail.geoUnsupported": "Geolocation isn't supported in this browser.",
  "detail.shareRefused": "Couldn't share your location.",
  "detail.shareDropped": "Lost the connection while sharing your location — still trying.",
  "detail.geoDenied": "Location permission denied — allow it in your browser settings.",
  "detail.geoUnavailable": "Location unavailable right now.",
  "detail.geoTimeout": "Location request timed out.",

  "detail.notFound": "Ride not found",
  "detail.backToRides": "Back to rides",
  "detail.back": "Back",
  "detail.isTripHome": "This is the trip home",
  "detail.hasTripHome": "There's a trip home too",
  "detail.seeRideOut": "See the ride out this morning",
  "detail.leavesBookSeparately": "Leaves {time} — book it separately",
  "detail.cantShowLocation": "We can't show where you are",
  "detail.stillLooking": "Still looking for your location",

  "detail.nothingToChange.title": "Nothing to change",
  "detail.nothingToChange.body": "The ride is already as you've set it, so nobody was notified.",
  "detail.changed.departure": "Departure is now {when}.",
  "detail.changed.pickup": "The pickup point moved — fuel and tolls were recalculated.",
  "detail.changed.seats": { one: "You're now offering 1 seat.", other: "You're now offering {n} seats." },
  "detail.changed.returnRemoved": "The return trip was removed — riders will need another way back.",
  "detail.changed.returnMoved": "The return time changed.",
  "detail.changed.returnAdded": "A return trip was added.",
  "detail.changed.notes": "Your notes were updated.",
  "detail.updated.title": "Ride updated",
  "detail.updated.notified": "Everyone holding or waiting on a seat has been notified.",
  "detail.updated.notNotified": "Nobody was notified — notes alone don't change the arrangement.",

  "detail.cancelRide.title": "Cancel this ride?",
  "detail.cancelRide.withRiders": {
    one: "1 rider is counting on this ride. They'll be notified straight away and will need to find another way in. This can't be undone.",
    other: "{n} riders are counting on this ride. They'll be notified straight away and will need to find another way in. This can't be undone.",
  },
  "detail.cancelRide.noRiders": "Nobody has booked a seat yet, so no one will be left without a lift. This can't be undone.",
  "detail.cancelRide.confirm": "Cancel the ride",
  "detail.cancelRide.keep": "Keep it",

  "detail.driverSubtitle": "OTE Group · driver",
  "detail.call": "Call {name}",
  "detail.callShort": "Call",
  "detail.walkToPickup": "Walk to pickup",
  "detail.arrives": "Arrives",
  "detail.leavesTraffic": "leaves {time} · traffic expected then",
  "detail.leavesEstimated": "leaves {time} · estimated",
  "detail.freeRide": "Free ride",
  "detail.yourShare": "Your share",
  "detail.youWaived": "you waived the cost split",
  "detail.driverWaived": "driver waived the cost split",
  "detail.splitWays": "€{total} split {n} ways",
  "detail.allYours": "all yours for now — €{each} each if one colleague joins",
  "detail.splitTwoWays": "€{total} split two ways once you join",
  "detail.booked": "{booked}/{total} booked",
  "detail.returnAt": "Return {time}",

  "detail.findAnother": "Find another",
  "detail.clash.title": "You're already on a ride that day",
  "detail.clash.body":
    "You have a seat on {name} at {when}. Leave that one first if you'd rather take this one — holding two seats keeps a colleague off both.",
  "detail.anotherRide": "another ride",
  "detail.goToThatRide": "See that ride",
  "detail.requestAgain": "Request again",
  "detail.requestToJoin": "Request to join",
  "detail.cancelMyRequest": "Cancel my request",
  "detail.leaveRide": "Leave this ride",
  "detail.stopSharing": "Stop sharing",
  "detail.shareLocation": "Share location",
  "detail.more": "More",
  "detail.tooCloseToEdit": "Too close to departure to change this ride.",
  "detail.changeDetails": "Change details",
  "detail.cancelRideShort": "Cancel ride",
  "detail.locked": "This ride is closed — no more changes.",
  "detail.riders": "Riders",
  "detail.noBookings": "No bookings yet.",
  "detail.acceptRequest": "Accept {name}'s request",
  "detail.rejectRequest": "Reject {name}'s request",
  "detail.thisRider": "this rider",

  "detail.live.offline": "Live feed disconnected",
  "detail.live.reconnecting": "Reconnecting to live feed…",
  "detail.live.waiting": "Waiting for first GPS fix…",
  "detail.live.sharing": "Sharing your location · {kph} km/h",
  "detail.live.driverMoving": "Driver moving · {kph} km/h",
  "detail.live.updated": "updated {time}",
  // Map marker labels — read aloud by screen readers and shown in the info window.
  "detail.marker.driverLive": "Driver — live",
  "detail.marker.youAreHere": "You are here",
  // Split on {place} at the call site so the place name can be bold — the placeholder must survive.
  "rides.usingInstead": "Using {place} instead of your home",
  "rides.showFull": { one: "+ 1 full ride", other: "+ {n} full rides" },
  "rides.hideFull": "Hide full rides",
  "rides.marker.office": "OTE HQ",
  "rides.marker.driverLeaves": "{name} · leaves {time}",
  "rides.yourHome": "your home",
  "office.name": "OTE HQ — Kifisias 99, Marousi",
  "offer.destinationIs": "Destination is",
  "offer.adjusted.distance": "Distance {from} → {to}",
  "offer.adjusted.tolls": "Tolls €{from} → €{to}",
  "offer.driveMins": "~{n} min drive",
  "rides.marker.yourPickup": "Your pickup point",
  "rides.marker.home": "Your home",

  // ── Network failures ──────────────────────────────────────────────────────────────────────────
  // Only reached when the server said nothing usable; its own messages arrive already translated.
  "http.generic": "That didn't work. Try again.",
  "http.timeout": "That took too long to answer. Check your connection and try again.",
  "http.offline": "Can't reach the server. Check your connection and try again.",
  "http.400": "Something in that request wasn't right. Check the form and try again.",
  "http.401": "Your session has ended. Sign in again.",
  "http.403": "You don't have access to that.",
  "http.404": "We couldn't find that — it may have been cancelled or removed.",
  "http.409": "Someone else got there first. Refresh to see where things stand.",
  "http.413": "That's too large to send.",
  "http.429": "Too many attempts. Wait a moment before trying again.",
  "http.500": "Something went wrong on our side. Try again in a moment.",
  "http.502": "The server is unreachable right now. Try again in a moment.",
  "http.503": "The server is busy or restarting. Try again in a moment.",
  "http.504": "The server took too long to answer. Try again in a moment.",

  // ── Navigation ────────────────────────────────────────────────────────────────────────────────
  "nav.home": "Home",
  "nav.rides": "Rides",
  "nav.notifications": "Notifications",
  "nav.leaders": "Leaders",
  "nav.profile": "Profile",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",
  "nav.menu": "Navigation menu",
  "nav.loading": "Loading your T-Ride…",
  "nav.badge.awaiting": { one: "1 awaiting your decision", other: "{n} awaiting your decision" },
  "nav.badge.unread": { one: "1 unread", other: "{n} unread" },
  "nav.pointsAndStreak": "{points} pts · {weeks}w streak",

  // ── Sign in ───────────────────────────────────────────────────────────────────────────────────
  "auth.workEmail": "Work email",
  "auth.password": "Password",
  "login.pageTitle": "Sign in",
  "login.welcomeBack": "Welcome back",
  "login.welcomeHint": "Sign in to keep sharing rides into Marousi.",
  "login.blurb":
    "T-Ride helps you share your commute with colleagues in Marousi. It's running as a trial for now. Sign in with your work email to find your colleagues' rides.",
  "login.fromNetwork": "FROM THE NETWORK",
  "login.quote.1": "Maria from OTE has carpooled 47 colleagues this month.",
  "login.quote.2": "Saved €34 commuting to Marousi by sharing my Tue-Thu rides.",
  "login.quote.3": "Our team's CO₂ from commuting dropped 28% in 6 weeks.",
  "login.forgot": "Forgot?",
  "login.signIn": "Sign in",
  "login.sessionExpired": "Your session expired. Please sign in again.",
  "login.passwordChanged": "Password changed. Sign in with your new password.",
  "login.failed": "Couldn't sign in. Check your details.",
  "login.demo.title": "TEST ACCOUNTS (FOR TESTING ONLY)",
  "login.demo.hint": "Tap one to fill it in — password",

  // ── Reset password ────────────────────────────────────────────────────────────────────────────
  "password.strength.tooShort": "Too short",
  "password.strength.weak": "Weak",
  "password.strength.ok": "OK",
  "password.strength.good": "Good",
  "password.strength.strong": "Strong",
  "reset.pageTitle": "Reset password",
  "reset.step1.title": "Forgot your password?",
  "reset.step1.hint": "Enter your work email and we'll send you a 6-digit code to reset it.",
  "reset.sendCode": "Send code",
  "reset.step2.title": "Check your email",
  "reset.step2.hint": "If an account exists for {email}, we've sent a 6-digit code. It expires in 10 minutes.",
  "reset.codeLabel": "6-digit code",
  "reset.verifyCode": "Verify code",
  "reset.changeEmail": "Change email",
  "reset.resendCode": "Send me a new code",
  "reset.step3.title": "Set a new password",
  "reset.step3.hint": "At least 8 characters. You'll be signed out of all other devices.",
  "reset.newPassword": "New password",
  "reset.confirmPassword": "Confirm password",
  "reset.submit": "Reset password",
  "reset.err.send": "Couldn't send a code. Try again.",
  "reset.err.code": "That code isn't valid. Check it and try again.",
  "reset.err.mismatch": "Passwords don't match.",
  "reset.err.tooShort": "Password must be at least 8 characters.",
  "reset.err.failed": "Couldn't reset your password. Start over.",

  // ── Dashboard ─────────────────────────────────────────────────────────────────────────────────
  // The greeting and the name are one string, not a greeting glued to a comma: Greek greets with
  // "Γεια σου" plus a name and the comma sits differently, which concatenation cannot express.
  "dash.title": "Hello, {name}",
  "dash.nextRideIs": "Your next ride is {when}.",
  "dash.postedRides": {
    one: "1 colleague posted a ride — pick one heading your way.",
    other: "{n} colleagues posted rides — pick one heading your way.",
  },
  "dash.quietDay": "Quiet day on the network — drivers usually post nightly.",

  "dash.countdown.departed": "departed",
  "dash.countdown.minutes": "in {n} min",
  "dash.countdown.hours": "in {h}h",
  "dash.countdown.hoursMinutes": "in {h}h {m}m",
  "dash.countdown.tomorrow": "tomorrow",
  "dash.countdown.days": "in {n} days",

  "dash.walk": "{mins} walk",
  "dash.transit": "{mins} transit",
  "dash.headline.driving": "Driving {booked}/{total} riders",
  "dash.headline.requestSent": "Request sent to {name}",
  "dash.headline.ridingWith": "Riding with {name}",

  "dash.yourNextRide": "YOUR NEXT RIDE",
  "dash.rideNofM": "RIDE {n} OF {total}",
  "dash.state.pending": "PENDING",
  "dash.state.driving": "DRIVING",
  "dash.state.confirmed": "CONFIRMED",
  "dash.pendingHint": "We'll notify you when the driver confirms — your seat isn't reserved yet.",
  "dash.leavesFrom": "Leaves {time} from {place}",
  "dash.pickUpAt": "Pick up at {place} · leaves {time}",
  "dash.yourPickup": "your pickup",
  "dash.greeting": "Welcome back",
  "dash.seatsTaken": "{booked}/{total} seats taken",
  "dash.free": "Free",
  "dash.showDetails": "Show details",
  "dash.hideDetails": "Hide details",
  "dash.prevRides": "Previous rides",
  "dash.nextRides": "Next rides",

  "dash.ridepoints": "RIDEPOINTS",
  "dash.seeRanking": "See ranking",
  "dash.stat.saved": "Saved",
  "dash.stat.co2": "CO₂ avoided",
  "dash.stat.rides": "Rides",

  "dash.noRideBooked": "No ride booked yet",
  "dash.upcomingRides": {
    one: "1 upcoming ride into the office — take a look.",
    other: "{n} upcoming rides into the office — pick one near you.",
  },
  "dash.noUpcomingRides": "No upcoming rides yet — be the first to post one.",
  // Fallbacks for a failed load, when humanError has nothing more specific (no network or status sentence).
  "dash.feedLoadFailed": "Couldn't load the rides. Try again in a moment.",
  "dash.commitmentsLoadFailed": "Couldn't load your rides. Try again in a moment.",
  "dash.findRide": "Find a ride",
  "dash.offerRide": "Offer a ride",

  "dash.setHome.cta": "Set your home address in Profile",
  "dash.setHome.why": " so these can be ranked by how far the pickup is from you.",
  "dash.setHome.ai": "For now they're ranked on your usual times and who you've ridden with.",
  "dash.setHome.heuristic": "Until then they're just ordered by whichever leaves soonest.",
  "dash.matches.ai": "AI matches for you",
  "dash.matches.smart": "Smart matches for you",
  "dash.matches.soonest": "Leaving soonest",
  "dash.seeAll": "See all →",

  "dash.loader.title": "Finding your best matches",
  "dash.loader.pickup": "Reading your pickup point…",
  "dash.loader.history": "Studying your past rides…",
  "dash.loader.matching": "Matching the best trips for you…",
  "dash.loader.ranking": "Almost ready — ranking by fit…",

  // ── Leaderboard ───────────────────────────────────────────────────────────────────────────────
  "leaders.title": "Leaderboard",
  // Single-company mode renders only the individuals board — see Leaderboard.js.
  "leaders.subtitle": "The colleagues moving Marousi forward, one ride at a time.",
  "leaders.yourRanking": "YOUR RANKING",
  "leaders.pts": "pts",
  "leaders.pointsShort": { one: "1 point", other: "{n} points" },
  "leaders.delta.week": "+{n} this week",
  "leaders.delta.month": "+{n} this month",
  "leaders.delta.period": "+{n} this period",
  // Both placeholders are consumed by a split in the view — keep {points} before {name}.
  "leaders.overtake": "{points} to overtake {name} — that's about {drives} more drives.",
  "leaders.topSpot": "You're #1 — defend your spot this week.",
  // See the Greek entry: for a reader ranked past the end of the trimmed board.
  "leaders.offBoard": "The board shows fewer places than yours — your position still counts.",
  // Shown when the board has loaded and the reader simply is not on it for that window.
  "leaders.notRanked.title": "You're not on this board yet",
  "leaders.notRanked.windowHint": "You haven't earned points in this period. Join or offer a ride and you'll appear here.",
  "leaders.notRanked.allHint": "Take your first shared ride and you'll show up in the standings.",
  "leaders.empty.title": "Nothing on the board yet",
  "leaders.empty.windowHint": "Nobody has earned points in this period. Try a longer one, or be the first.",
  "leaders.empty.allHint": "Once colleagues start sharing rides, the standings appear here.",
  "leaders.timeframe.week": "This week",
  "leaders.timeframe.month": "This month",
  "leaders.timeframe.all": "All time",
  "leaders.you": "YOU",
  "leaders.podium": "Top of the leaderboard",
  "leaders.rules.title": "How points work",
  "leaders.rules.carry": "for each passenger you take along",
  "leaders.rules.join": "for each ride you take as a passenger",

  // ── Rides list ────────────────────────────────────────────────────────────────────────────────
  "rides.later": "Later",
  "rides.title.today": "Today's rides",
  "rides.title.tomorrow": "Tomorrow's rides",
  "rides.title.later": "Coming days",
  "rides.subtitle.sorted": "Sorted by distance from your home — pick a seat and head to work without driving.",
  "rides.subtitle.setHome": "Set your home address in Profile to see rides closest to you first.",
  "rides.yourUpcoming": "Your upcoming rides",
  "rides.searchPlaceholder": "Another starting point",
  // See the Greek entry: this was hard-coded in views/Rides.js and never reached the catalogue.
  "rides.jumpTo": "JUMP TO LOCATION",
  "rides.clearSearch": "Clear search",
  "rides.resetToHome": "Reset to home",
  "rides.resetToHomeAria": "Reset to home address",
  "rides.sort": "SORT",
  "rides.closestTo": "Closest to {place}",
  "rides.closestToHome": "Closest to your home",
  "rides.earliest": "Earliest",
  "rides.ranking": "Ranking…",
  "rides.aiMatch": "Best for you",
  "rides.freeOnly": "Free only",
  "rides.showMap": "Show map",
  "rides.hideMap": "Hide map",
  "rides.clearFocus": "Clear focus",
  "rides.showAll": "Show all",
  "rides.empty.today": "No rides today",
  "rides.empty.tomorrow": "No rides tomorrow",
  "rides.empty.later": "No rides scheduled later",
  "rides.empty.driverHint": "Be the first — post a ride and earn +{n} points per rider.",
  "rides.empty.riderHint": "Try a different day, or check back later — drivers post nightly.",
  // Three letters, because they sit in a fixed 40px square avatar slot.
  "rides.drvShort": "DRV",
  "rides.rdrShort": "RDR",
  "rides.waitingOn": "Waiting for {name} to answer",
  "rides.confirmedWith": "Confirmed with {name}",
  "rides.leavesAt": "Leaves {time}",
  "rides.roleDriver": "Driver",
  "rides.rolePending": "Pending",
  "rides.roleRider": "Rider",

  // ── Ride card ─────────────────────────────────────────────────────────────────────────────────
  "ride.today": "Today",
  "ride.tomorrow": "Tomorrow",
  "ride.mins": "{m} min",
  "ride.hours": "{h} h",
  "ride.hoursMins": "{h} h {m} min",
  "ride.dayAndDeparture": "{day} · leaves {time}",
  "ride.arrives": "arrives ~{time}",
  "ride.yours": "YOUR RIDE",
  "ride.requested": "REQUESTED",
  "ride.confirmed": "CONFIRMED",
  "ride.rideWord": "ride",
  "ride.yourShare": "your share",
  "ride.full": "Full",
  "ride.seatsLeft": { one: "1 seat", other: "{n} seats" },

  // ── Notifications ─────────────────────────────────────────────────────────────────────────────
  "notif.title": "Notifications",
  "notif.subtitle": "Join requests, confirmations and ride updates land here.",
  "notif.loadFailed": "Couldn't load notifications.",
  "notif.actionFailed": "Couldn't do that just now.",
  "notif.empty.title": "All caught up",
  "notif.empty.hint": "Notifications about your rides and join requests will appear here.",
  "notif.section.today": "Today",
  "notif.section.week": "This week",
  "notif.section.older": "Older",
  "notif.showLess": "Show less",
  "notif.showAll": "Show all",
  "notif.showAllMore": "Show all ({n} more)",
  "notif.earned": "You earned {points}",
  "notif.points": { one: "+1 point", other: "+{n} points" },
  "notif.free": "FREE",
  "notif.pickup": "Pickup",
  "notif.accept": "Accept",
  "notif.reject": "Reject",

  // Keyed on the notification kind, so adding a kind server-side means adding entries here rather
  // than editing the view. `fallback` covers a kind this catalogue hasn't been taught yet.
  "notif.card.fallback": "Ride update",
  "notif.card.INCOMING_REQUEST": "{name} wants to join your ride",
  "notif.card.BOOKING_CONFIRMED": "{name} confirmed your ride",
  "notif.card.BOOKING_REJECTED": "{name} declined your request",
  "notif.card.RIDE_CANCELLED": "{name} cancelled the ride",
  "notif.card.BOOKING_CANCELLED": "{name} withdrew from your ride",
  "notif.card.BOOKING_COMPLETED": "Your ride with {name} is complete",
  "notif.card.RIDE_COMPLETED": "You drove {name}",
  "notif.card.RIDE_CHANGED": "{name} changed a ride you're on",
  "notif.card.DEPARTURE_REMINDER": "Your ride leaves soon",

  "notif.badge.INCOMING_REQUEST": "PENDING",
  "notif.badge.BOOKING_CONFIRMED": "CONFIRMED",
  "notif.badge.BOOKING_REJECTED": "DECLINED",
  "notif.badge.RIDE_CANCELLED": "CANCELLED",
  "notif.badge.BOOKING_CANCELLED": "WITHDRAWN",
  "notif.badge.BOOKING_COMPLETED": "RIDE COMPLETE",
  "notif.badge.RIDE_COMPLETED": "RIDE COMPLETE",
  "notif.badge.RIDE_CHANGED": "UPDATED",
  "notif.badge.DEPARTURE_REMINDER": "LEAVING SOON",

  "notif.resolution.CONFIRMED": "ACCEPTED",
  "notif.resolution.REJECTED": "DECLINED",
  "notif.resolution.CANCELLED": "WITHDRAWN",

  // ── Profile ───────────────────────────────────────────────────────────────────────────────────
  "profile.title": "Profile",
  "profile.subtitle":
    "Your T‑Ride identity — set where you start from, your phone, and how you commute.",
  "profile.org": "OTE Group",
  "profile.role.driverRider": "Driver · Rider",
  "profile.role.riderOnly": "Rider only",
  "profile.points": "POINTS",
  "profile.streak": "{n}w streak",

  "profile.pickup.label": "USUAL PICKUP POINT",
  "profile.pickup.hint":
    "We sort the rides near you from here. A driver sees it only when you book a seat with them without choosing another point.",
  "profile.pickup.set": "Set your usual pickup point",
  "profile.pickup.change": "Change your usual pickup point",
  "profile.pickup.stopChanging": "Stop changing your usual pickup point",
  "profile.pickup.blurb":
    "We sort the rides near you from here. A driver sees it only when you book a seat with them without choosing another point — so pick a corner or a stop, not your front door.",
  "profile.pickup.searchPlaceholder": "Search a street, corner or landmark…",
  "profile.pickup.currentLocation": "Current location",
  "profile.pickup.popular": "POPULAR PICKUPS",
  "profile.pickup.discard": "Discard selected address",
  "profile.pickup.saveFailed": "Couldn't save those changes.",
  "profile.pickup.geo.unsupported": "Geolocation not supported by your browser.",
  "profile.pickup.geo.resolveFailed": "Couldn't resolve address — try typing it.",
  "profile.pickup.geo.denied": "Permission denied — allow location access in your browser.",
  "profile.pickup.geo.unavailable": "Location unavailable.",
  "profile.pickup.geo.timeout": "Location request timed out.",
  "profile.pickup.geo.error": "Geolocation error.",

  "profile.phone.label": "PHONE NUMBER",
  "profile.phone.title": "Phone number",
  "profile.phone.notSet": "Not set — confirmed co-riders won't be able to call you.",
  "profile.phone.visibility": "Visible only to people you've matched with on a ride.",
  "profile.phone.format": "Use international format. Greek mobiles look like",
  "profile.phone.placeholder": "+30 694 123 4567",
  "profile.phone.invalid": "That doesn't look like a valid phone number.",
  "profile.phone.saveFailed": "Couldn't save your phone number.",
  "profile.phone.cancelAria": "Cancel editing phone number",

  "profile.driver.title": "I drive to work",
  "profile.driver.hint":
    "You can always join rides as a passenger. Turning this on opens \"Your car\" below, which prices what everyone chips in.",

  "profile.car.title": "Your car",
  "profile.car.hint": "What everyone chips in — you included — is worked out from this.",
  "profile.car.hintSet": "Keep it close to what you actually pay.",
  "profile.car.hintUnset": "Start with what it runs on.",
  "profile.car.fuelQuestion": "What does it run on?",
  "profile.car.fuelSet": "✓ set",
  "profile.car.fuelPick": "· pick one",
  "profile.car.pickOneHint": "Pick one — it sets the unit for both figures below.",
  "profile.car.consumption": "Consumption",
  "profile.car.pricePaid": "Price you last paid",
  "profile.car.perKm": "Your rides cost {price} per km",
  "profile.car.electricFree":
    "Charging at work? Enter {zero} and your rides cost passengers nothing.",
  "profile.car.saveFailed": "Couldn't save your car.",
  "profile.car.increase": "Increase {field}",
  "profile.car.decrease": "Decrease {field}",
  "profile.car.outOfBand":
    "Most are between {low} and {high} {unit}. Fine if that's really what you pay — passengers are charged from this.",

  "fuel.COMBUSTION": "Petrol / Diesel / Hybrid / LPG",
  "fuel.ELECTRIC": "Electric",

  "profile.rideEmails.title": "Ride emails",
  "profile.rideEmails.hint":
    "Join requests, confirmations, cancellations and a reminder before departure. Notifications always appear in the app — this only controls your inbox. Security emails such as password resets are always sent.",
  "profile.rideEmails.aria": "Ride email frequency",
  "emailPref.INSTANT.label": "Each event",
  "emailPref.INSTANT.hint": "An email as things happen",
  "emailPref.DIGEST.label": "Daily digest",
  "emailPref.DIGEST.hint": "One summary each morning",
  "emailPref.OFF.label": "Off",
  "emailPref.OFF.hint": "In-app notifications only",

  "profile.appearance.title": "Appearance",
  "profile.appearance.hint": "Applies straight away, on this device. The map picks up the same choice.",
  "profile.appearance.aria": "Colour theme",
  "theme.light.label": "Light",
  "theme.light.hint": "Always the light theme",
  "theme.dark.label": "Dark",
  "theme.dark.hint": "Always the dark theme",
  "theme.system.label": "System",
  "theme.system.hint": "Follows your phone or computer, and changes with it",

  "profile.uiVersion.title": "Interface version",
  "profile.uiVersion.hint": "Applies to everyone, including the sign-in page. Takes effect straight away.",
  "profile.uiVersion.aria": "App interface version",
  "profile.uiVersion.v1.label": "Version 1",
  "profile.uiVersion.v1.hint": "The original look, with see-through panels",
  "profile.uiVersion.v2.label": "Version 2",
  "profile.uiVersion.v2.hint": "The new look, with white cards and rounded buttons",
  "profile.uiVersion.saveFailed": "Couldn't change the version — the previous one still applies.",

  "profile.language.title": "Language",
  "profile.language.hint":
    "Changes the app, and the emails T‑Ride sends you, straight away. It follows your account, so it applies on every device you sign in on.",
  "profile.language.aria": "App language",
  "profile.language.saveFailed": "Couldn't save your language — it will revert when you reload.",
  "profile.driver.saveFailed": "The change wasn't saved. Check your connection and flip the switch again.",
  "profile.rideEmails.saveFailed": "Your email choice wasn't saved. Check your connection and choose again.",
  // Each language names itself in itself. Someone stranded in the language they don't read has to
  // be able to find their way out, and "Ελληνικά" is legible as a label even to a non-reader of Greek.
  // `.short` is the two-letter form for the header pill, where the full name will not fit.
  "profile.suggestion.halandri": "Halandri Metro Station",
  "profile.suggestion.kifisia": "Kifisia Square",
  "profile.suggestion.ambelokipoi": "Ambelokipoi Metro",

  "language.el.short": "ΕΛ",
  "language.el.label": "Ελληνικά",
  "language.el.hint": "Η γλώσσα της εφαρμογής και των email σου",
  "language.en.short": "EN",
  "language.en.label": "English",
  "language.en.hint": "The language of the app and your emails",

  // ── Public pages (header controls) ────────────────────────────────────────────────────────────
  "public.themeToDark": "Switch to dark theme",
  "public.themeToLight": "Switch to light theme",

  "profile.pastRides.title": "Your past rides",
  "profile.pastRides.seeAll": "See all {n} →",
  "profile.pastRides.empty": "No completed rides yet — your first one will land here.",

  "profile.signedInAs": "Signed in as {email}",
  "profile.signOut": "Sign out",

  // Profile photo. The error sentences mirror the server's avatar.* messages word for word.
  "profile.photo.add": "Add photo",
  "profile.photo.change": "Change photo",
  "profile.photo.remove": "Remove",
  "profile.photo.hint": "Only colleagues who are signed in can see it. JPEG or PNG, up to 25 MB.",
  "profile.photo.removeTitle": "Remove your photo?",
  "profile.photo.removeBody": "Your initials will show instead.",
  "profile.photo.saveFailed": "Your photo wasn't saved. Try again.",
  "profile.photo.removeFailed": "Your photo wasn't removed. Try again.",
  "profile.photo.error.empty": "No photo was chosen. Pick an image file.",
  "profile.photo.error.tooLarge": "That photo is too large. Pick a file up to {mb} MB.",
  "profile.photo.error.type": "That file isn't a photo that can be opened here. Pick a JPG or PNG photo and try again.",
  "profile.photo.error.webp": "This photo type (WebP) can't be opened here. Save it as JPG or PNG and try again.",
  "profile.photo.error.unreadable": "We couldn't read that photo — it may be damaged. Try another one.",

  "profile.danger.title": "Danger zone",
  "profile.danger.hint":
    "Deleting your account cancels any ride you're driving and erases your personal details. Completed trips stay in your colleagues' history without your name on them. There's no undo.",
  "profile.danger.delete": "Delete my account",
  "profile.danger.deleteFailed": "Couldn't delete your account.",

  "deleteAccount.title": "Delete your account?",
  "deleteAccount.cancelledWord": "cancelled",
  "deleteAccount.point.rides": "Any ride you're driving will be {cancelled}, and your riders told.",
  "deleteAccount.point.seats": "Seats you hold on other people's rides will be given back.",
  "deleteAccount.point.details": "Your name, email, phone and home address are erased.",
  "deleteAccount.point.history":
    "Trips you already completed stay in your colleagues' history — anonymously, because they are their records too.",
  "deleteAccount.noUndo": "This can't be undone — the account can't be restored.",
  "deleteAccount.passwordLabel": "Enter your password to confirm",
  "deleteAccount.keep": "Keep my account",
  "deleteAccount.confirm": "Delete account",

  "notFound.pageTitle": "Not found",
  "notFound.title": "We couldn't find this page",
  "notFound.body": "The link may be old or mistyped. Go back home and carry on from there.",
  "notFound.publicBody": "We couldn't find this page. The link may be old or mistyped. Go back home and carry on from there.",
  "notFound.backToDashboard": "Back to dashboard",
  "notFound.backHome": "Back home",

  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "reset.backToSignIn": "Back to sign in",
  "dash.goToPosition": "Go to position {n} of {total}",

  "profile.footer.feedback": "Send feedback",
  "profile.footer.version": "v0.1 · T‑Ride pilot",

  // Roles and the admin screen.
  "nav.pointsSystem": "Points system",
  "admin.title": "Points system",
  "admin.subtitle": "Set how many points a ride gives, what the leaderboard counts and which rewards colleagues can earn.",
  "admin.saved": "Settings saved.",
  "admin.save": "Save changes",
  "admin.saving": "Saving…",
  "admin.discard": "Discard changes",
  "admin.you": "you",
  "admin.lastChanged": "Last changed {when} by {who}.",
  "admin.err.load": "Could not load the settings.",
  "admin.err.save": "Could not save the settings.",
  "admin.points.title": "Points",
  "admin.points.help": "Awarded when a ride completes. Changing these affects future rides only — points already earned stay as they were.",
  "admin.points.rider": "Points per rider",
  "admin.points.riderHint": "Every confirmed rider gets these once the ride is completed. Colleagues see this in the “How points work” card on the leaderboard.",
  "admin.points.driverPerRider": "Driver points per rider carried",
  "admin.points.driverPerRiderHint": "The driver gets these for each rider they took along — three riders, three times. Colleagues see this in the “How points work” card on the leaderboard.",
  "admin.points.driverCompletion": "Driver points for completing a ride",
  "admin.points.driverCompletionHint": "The driver gets these once for each ride they complete, however many rode. Set it above 0 to also reward a driver who offered seats but nobody came. Colleagues see this in the “How points work” card.",
  "admin.ranking.title": "Leaderboard",
  "admin.ranking.help": "How many days each leaderboard tab looks back, and how many colleagues it lists.",
  "admin.ranking.week": "“Week” covers (days)",
  "admin.ranking.weekHint": "How many recent days the “This week” tab on the leaderboard counts.",
  "admin.ranking.month": "“Month” covers (days)",
  "admin.ranking.monthHint": "How many recent days the “This month” tab on the leaderboard counts.",
  "admin.ranking.boardSize": "Places shown",
  "admin.ranking.boardSizeHint": "How many colleagues the leaderboard lists.",
  "admin.rewards.title": "Rewards you earn once",
  "admin.rewards.summary": "{n} points",
  "admin.recurring.title": "Rewards you earn again and again",
  "admin.recurring.help": "Every time someone collects this many more points, they earn it again. Colleagues see these on the leaderboard.",
  "admin.recurring.empty": "None yet. Add one to give something away again and again.",
  "admin.recurring.every": "Every how many points",
  "admin.recurring.everyHint": "Each time someone's total completes another interval this size, they earn the reward again.",
  "admin.recurring.maxGrants": "Limit per person",
  "admin.recurring.maxGrantsHint": "The most times one person can ever earn it. Leave it 0 for no limit.",
  "admin.recurring.summary": "every {n} points",
  "admin.recurring.add": "Add a reward you can earn again",
  "admin.rewards.help": "How many points each reward needs and what the colleague gets. Colleagues see these on the leaderboard.",
  "admin.rewards.empty": "None yet. Colleagues see no rewards until you add one.",
  "admin.rewards.threshold": "Points needed",
  "admin.rewards.label": "Reward name",
  "admin.rewards.description": "What they get",
  "admin.rewards.provider": "Handled by",
  "admin.rewards.enabled": "Offered",
  "admin.rewards.remove": "Remove",
  "admin.rewards.addTier": "Add a reward",
  "admin.rewards.addField": "Add a detail",
  "admin.rewards.payload": "Details (free text — not used yet)",
  "offer.route.via": "via {road}",

  // Live scheme rules on the leaderboard, and the reward-provider settings.
  "leaders.rules.complete": "for each ride you complete as the driver",
  "leaders.rules.rewards": "REWARDS",
  "admin.stepper.increase": "Increase {field}",
  "admin.stepper.decrease": "Decrease {field}",
  "admin.rewards.optional": "(optional)",
  "admin.provider.box.name": "Box",
  "admin.provider.box.hint": "Gives a Box coupon. For now it is a test: a code is made, but Box is not told yet.",

  // The reward ladder on the leaderboard.
  "rewards.title": "REWARDS",
  "rewards.collapsed.next": "{n} pts to {reward}",
  "rewards.collapsed.none": "All claimed",
  "rewards.section.recurring": "COMES ROUND AGAIN",
  "rewards.section.recurringHint": "Every time you complete the points, you earn it again.",
  "rewards.section.ladder": "GOALS",
  "rewards.section.ladderHint": "Earned once, the moment you reach the threshold.",
  "rewards.ladderTitle": "Reward goals",
  "rewards.ladder.moreEarned": { one: "+1 more earned", other: "+{n} more earned" },
  "rewards.ladder.hideEarned": "Hide older",
  "rewards.recurring.completedCount": { one: "1 completed reward", other: "{n} completed rewards" },
  "rewards.recurring.hideCompleted": "Hide completed",
  "rewards.recurring.every": "every {n} pts",
  "rewards.recurring.everyCapped": "every {n} pts · up to {max} times",
  "rewards.recurring.toNext": "points to your next one",
  "rewards.recurring.earnedSoFar": { one: "1 so far", other: "{n} so far" },
  "rewards.recurring.allClaimed": { one: "Claimed — that was the only one.", other: "All {n} claimed." },
  "rewards.recurring.ridesToGo": { one: "1 more shared ride", other: "About {n} more shared rides" },
  "rewards.toGo": "points to {reward}",
  "rewards.allEarned": "You have earned every reward — nice work.",
  "rewards.earned": "Earned",
  "rewards.earnedCount": { one: "1 earned", other: "{n} earned" },
  "admin.err.missing": "Fill in the required fields first: {fields}",
  "admin.rewards.field.couponAmount": "Coupon amount",
  "admin.rewards.field.couponUnit": "Amount is",
  "admin.rewards.unit.EUR": "euros (€)",
  "admin.rewards.unit.PERCENT": "percent (%)",
  "admin.rewards.copyHint": "Both are shown to everyone on the leaderboard, so fill in each language.",
  "admin.rewards.thresholdHint": "The points someone must reach before this is earned.",
  "admin.rewards.field.couponAmount.placeholder": "e.g. 10",
  "admin.rewards.field.couponAmount.hint": "This reward is handed out as a coupon: type what it is worth, just the number, and choose euros or percent below. Colleagues don't see this number — if you want them to know it, put it in \"What they get\".",
  "admin.rewards.field.couponUnit.hint": "Decides whether 10 means €10 off or 10% off.",
  "admin.rewards.untitled": "Untitled reward",
  "admin.rewards.incomplete": "Incomplete",
  "admin.rewards.off": "Not offered",
  "rewards.ridesToGo": { one: "1 more shared ride", other: "About {n} more shared rides" },
  "rewards.away": "{n} to go",
  "rides.empty.driverHintPlain": "Be the first — post a ride for tomorrow morning.",
  "admin.rewards.eg": "e.g.",
  "admin.rewards.labelExample": "Gold coupon",
  "admin.rewards.descriptionExample": "A €10 Box voucher",

  // ── v2.common ── (keys for shared v2 primitives go directly below)
  "v2.common.seats.option": { one: "1 seat", other: "{n} seats" },
  "v2.common.seats.taken": { one: "1 seat is already taken — you can't offer fewer", other: "{n} seats are already taken — you can't offer fewer" },
  "v2.common.seats.tooFew": { one: "You already have 1 rider, so you can't offer {m}.", other: "You already have {n} riders, so you can't offer {m}." },
  // "i" explanations (components/v2/ui/InfoTip, views/v2/parts/explain). Plain words, 1–3 sentences.
  "v2.common.infoTip": "What does this mean?",
  "v2.explain.points.title": "How you earn points",
  "v2.explain.points.rider": { one: "As a passenger you get 1 point for each ride.", other: "As a passenger you get {count} points for each ride." },
  "v2.explain.points.driver": { one: "As a driver you get 1 point for each passenger you take.", other: "As a driver you get {count} points for each passenger you take." },
  "v2.explain.points.completion": { one: "Plus 1 point for every ride you complete as a driver.", other: "Plus {count} points for every ride you complete as a driver." },
  "v2.explain.points.generic": "You earn points every time a shared ride is completed, as a passenger or as a driver.",
  "v2.explain.points.when": "Points are added when the driver taps \"Complete\" at the end of the ride. Collect points and win rewards.",
  "v2.explain.streak.title": "Weeks in a row",
  "v2.explain.streak.body": "How many weeks in a row you've shared at least one ride. If a whole week goes by without one, the count starts again.",
  "v2.explain.share.title": "Your share",
  "v2.explain.share.split": "The ride's fuel and tolls are split equally between everyone in the car, the driver included.",
  "v2.explain.share.drops": "The more people join, the smaller it gets. The final amount is set when the ride is completed.",
  "v2.explain.share.pay": "The app doesn't take payments: you settle the amount with the driver directly.",
  "v2.explain.share.driver": "This is what each of your passengers pays. You pay the same share too, as you all travel together.",
  "v2.explain.share.driverEach": "What each person in the car pays, you included.",
  "v2.explain.share.payDriver": "The app doesn't handle payments; you settle it between you.",
  "v2.explain.free.title": "Free ride",
  "v2.explain.free.body": "The driver covers the fuel and tolls, so you pay nothing. Points count as usual.",
  "v2.explain.seats.title": "Seats",
  "v2.explain.seats.body": "How many passengers the driver can take. A seat is only taken once the driver accepts someone's request.",
  "v2.explain.seats.offer": "How many colleagues you can take. You decide whom to accept. Once the seats are full, the ride takes no more requests.",
  "v2.explain.suggest.title": "How we pick suggestions",
  "v2.explain.suggest.home": "You see first the rides that start closest to your home and leave soonest.",
  "v2.explain.suggest.habits": "The times you usually travel and drivers you've ridden with before can count too.",
  "v2.explain.suggest.noHome": "You haven't set an address, so the soonest rides come first. Set it in Profile to see the closest ones.",
  "v2.explain.sort.title": "How rides are sorted",
  "v2.explain.sort.nearest": "\"{label}\": rides that start nearest to you first.",
  "v2.explain.sort.earliest": "\"{label}\": in the order they leave.",
  "v2.explain.sort.match": "\"{label}\": we weigh how close each ride starts and how soon it leaves, and say under each ride why it suits you.",
  "v2.explain.sort.free": "\"{label}\": only rides where the driver covers the costs.",
  "v2.explain.lock.title": "Why the ride is closed",
  "v2.explain.lock.body": { one: "A ride closes when it's completed or 1 hour after it leaves.", other: "A ride closes when it's completed or {count} hours after it leaves." },
  "v2.explain.lock.bodyPlain": "A ride closes when it's completed or a few hours after it leaves.",
  "v2.explain.lock.why": "From then on nobody can join, leave or change anything, so the costs and points stay right. The driver can still tap \"Complete\".",
  "v2.explain.pending.title": "Waiting for an answer",
  "v2.explain.pending.rider": "You asked for a seat and the driver hasn't answered yet. You'll get a notification as soon as they accept or decline. Until then the seat isn't yours.",
  "v2.explain.pending.driverTitle": "Someone wants a seat",
  "v2.explain.pending.driver": "A colleague asked for a seat on your ride. It's your call: tap \"Accept\" or \"Reject\" and they're told straight away.",
  "v2.explain.periods.title": "Leaderboard periods",
  "v2.explain.periods.week": { one: "\"{label}\": points from the last day.", other: "\"{label}\": points from the last {n} days." },
  "v2.explain.periods.month": { one: "\"{label}\": points from the last day.", other: "\"{label}\": points from the last {n} days." },
  "v2.explain.periods.all": "\"{label}\": every point since the start.",
  "v2.explain.periods.place": "Your place changes with the period you pick.",
  "v2.explain.reward.title": "How you win rewards",
  "v2.explain.reward.goal": "You win a goal once, as soon as your total points reach its number.",
  "v2.explain.reward.recurring": "\"Again and again\" rewards come round each time you collect that many more points.",
  "v2.explain.reward.keep": "Your points are never spent: your total is what counts.",
  "v2.explain.impact.title": "What these figures mean",
  "v2.explain.impact.saved": "Saved: an estimate of what you saved on costs, about {rider} per ride as a passenger and {driver} as a driver.",
  "v2.explain.impact.co2": "CO₂: an estimate of the emissions avoided, about half a kilo per shared ride.",
  "v2.explain.impact.rides": "Rides: the shared rides you've completed, as a driver or a passenger.",
  "v2.explain.admin.title": "What colleagues see",
  "v2.explain.admin.body": "Whatever you save applies to everyone straight away and shows on Home and the Leaderboard. Points already given don't change.",
  "v2.explain.confirm.leaveTitle": "Leave this ride?",
  "v2.explain.confirm.leaveBody": "Your seat is freed and {name} is told. To get back on, you'll need to ask for a seat again.",
  "v2.explain.confirm.leaveKeep": "No, stay",
  "v2.explain.confirm.cancelRequestTitle": "Cancel your request?",
  "v2.explain.confirm.cancelRequestBody": "{name} won't see your request any more. You can ask for a seat again whenever you like.",
  "v2.explain.confirm.cancelRequestKeep": "No, keep it",
  "v2.explain.confirm.completeTitle": "Complete this ride?",
  "v2.explain.confirm.completeBody": "Tap this once you've arrived. The cost is split between everyone who was in the car and everyone gets their points. This can't be undone.",
  "v2.explain.confirm.completeKeep": "Not yet",
  // Public transport to the pickup (views/logic/transitWords, TransitLine, AccessCardV2).
  // Lines in words, not codes: "M3" → "Metro Line 3", "T7" → "Tram 7".
  "v2.transit.line.bus": "Bus {line}",
  "v2.transit.line.metro": "Metro Line {line}",
  "v2.transit.line.tram": "Tram {line}",
  "v2.transit.line.trolley": "Trolleybus {line}",
  "v2.transit.line.train": "Train {line}",
  "v2.transit.line.ferry": "Ferry {line}",
  "v2.transit.line.other": "Line {line}",
  "v2.transit.mode.bus": "Bus",
  "v2.transit.mode.metro": "Metro",
  "v2.transit.mode.tram": "Tram",
  "v2.transit.mode.trolley": "Trolleybus",
  "v2.transit.mode.train": "Train",
  "v2.transit.mode.ferry": "Ferry",
  "v2.transit.mode.other": "Public transport",
  "v2.transit.by.bus": "by bus",
  "v2.transit.by.metro": "by metro",
  "v2.transit.by.tram": "by tram",
  "v2.transit.by.trolley": "by trolleybus",
  "v2.transit.by.train": "by train",
  "v2.transit.by.ferry": "by ferry",
  "v2.transit.by.other": "by public transport",
  "v2.transit.by.any": "by public transport",
  "v2.transit.board": "Get on: {stop}",
  "v2.transit.change": "Change: {stop}",
  "v2.transit.alight": "Get off: {stop}",
  "v2.transit.step.walkToStop": "Walk to the {stop} stop",
  "v2.transit.step.walkToVehicle": "Walk to the next stop",
  "v2.transit.step.walkToPickup": "Walk to the pickup point",
  "v2.transit.step.take": "Take {line}",
  "v2.transit.step.takeTowards": "Take {line} towards {headsign}",
  "v2.transit.step.getOff": "Get off at {stop}",
  "v2.transit.step.getOffAfter": {
    one: "Get off at {stop}, after 1 stop",
    other: "Get off at {stop}, after {n} stops",
  },
  "v2.transit.step.pickupHere": "The driver picks you up here: {place}",
  "v2.transit.noun.bus": { one: "bus", other: "{n} buses" },
  "v2.transit.noun.metro": { one: "metro", other: "{n} metro lines" },
  "v2.transit.noun.tram": { one: "tram", other: "{n} tram lines" },
  "v2.transit.noun.trolley": { one: "trolleybus", other: "{n} trolleybuses" },
  "v2.transit.noun.train": { one: "train", other: "{n} trains" },
  "v2.transit.noun.ferry": { one: "ferry", other: "{n} ferries" },
  "v2.transit.noun.other": { one: "public transport", other: "{n} lines" },
  "v2.transit.combo.by": "by {modes}",
  "v2.transit.combo.changes": { one: "1 change", other: "{n} changes" },
  "v2.transit.combo.noChange": "no change",
  "v2.transit.combo.label": "{modes}, {changes}",
  "v2.transit.card.title": "How to get to the pickup point",
  "v2.transit.card.sub": "From your home to where the driver picks you up. Tap a way to see it on the map.",
  "v2.transit.card.subWalkOnly": "From your home to where the driver picks you up. The walk is drawn on the map.",
  "v2.transit.card.onFoot": "on foot",
  "v2.transit.card.faster": "{mins} quicker than walking",
  "v2.transit.card.noTransit": "We couldn't find public transport quicker than walking.",
  "v2.transit.card.subFar": "From your home to where the driver picks you up.",
  "v2.transit.card.farNoTransit": "The pickup is {km} from your home. It's too far to walk and we couldn't find public transport. Pick a ride that starts closer to you.",
  "v2.transit.card.showSteps": "Show the steps",
  "v2.transit.card.hideSteps": "Hide the steps",
  "v2.transit.card.neitherGood": "Neither way works for you? Pick a ride that starts closer to your home.",
  "v2.transit.card.tooFar": "Too far for you? Pick a ride that starts closer to your home.",
  "v2.transit.card.findAnother": "Find another ride",
  "v2.common.dialog.discardQuestion": "You have changes you haven't saved. If you close now, they'll be lost.",
  "v2.common.dialog.keepEditing": "Keep editing",
  "v2.common.dialog.discard": "Close without saving",
  "v2.common.map.controls": "Map controls",
  "v2.common.map.zoomIn": "Zoom in",
  "v2.common.map.zoomOut": "Zoom out",
  "v2.common.map.recentre": "Recentre the map",
  "v2.common.map.keyWalk": "on foot",
  "v2.common.map.keyTransit": "public transport",
  "v2.common.liveRoute": "live route",
  "v2.common.map.follow": "Follow me",
  "v2.common.map.followOn": "The map is following you — tap to stop",
  "v2.common.traffic.title": "Traffic",
  "v2.common.traffic.free": "clear",
  "v2.common.traffic.slow": "slow",
  "v2.common.traffic.jam": "jammed",
  "v2.common.traffic.hintAt": "The route colours show the traffic we expect at {time}.",
  "v2.common.traffic.hintNow": "The route colours show the traffic right now.",
  "v2.common.traffic.past": "No traffic colours",
  "v2.common.traffic.pastHint": "This ride has already happened, so the line shows no traffic.",
  "v2.common.decrease": "Decrease",
  "v2.common.increase": "Increase",
  "v2.common.required": "required",
  "v2.common.podiumEntry": "Place {n}: {name}, {points}",
  // ── v2.shell ── (keys for AppShellV2 / PublicLayoutV2 go directly below)
  "v2.shell.points": { one: "{points} point", other: "{points} points" },
  "v2.shell.pointsWord": { one: "point", other: "points" },
  "v2.shell.pointsLink": { one: "{points} point · open the leaderboard", other: "{points} points · open the leaderboard" },
  "v2.shell.streak": { one: "1-week streak", other: "{n}-week streak" },
  "v2.shell.accountMenu": "Account menu: {name}",
  "v2.shell.openProfile": "Open your profile",
  "v2.shell.navLeaderboard": "Leaderboard",
  // ── v2.dashboard ── (keys for DashboardV2 go directly below)
  "v2.dashboard.dayYouDrive": "you drive this day",
  "v2.dashboard.youDriveThatDay": { one: "You drive this day (you leave at {times}). You can only ask for a seat on a ride leaving more than 1 hour before or after.", other: "You drive this day (you leave at {times}). You can only ask for a seat on a ride leaving more than {n} hours before or after." },
  "v2.dashboard.drivingThen": "You drive then",
  "v2.dashboard.drivingThenWhy": "You leave at {time} on your own ride, so you can't be a passenger then too.",
  // A seat or a request the reader already holds on the same day, to the same place (the one-seat rule).
  // Shared with the rides list: badge first, then the tooltip that explains it.
  "v2.dashboard.seatThen": "You have a seat then",
  "v2.dashboard.seatThenWhy": "You already have a seat on {name}'s ride at {time}. You can hold one seat a day to the same place; if you'd rather take this one, leave that ride first.",
  "v2.dashboard.requestedThen": "You asked for a seat then",
  "v2.dashboard.requestedThenWhy": "You've already asked for a seat on {name}'s ride at {time}. You can ask for one seat a day to the same place; if you'd rather take this one, cancel that request first.",
  // The feed or the reader's own rides failed to load: the title, then humanError's sentence and a retry pill.
  "v2.dashboard.feedFailedTitle": "The rides didn't load.",
  "v2.dashboard.commitmentsFailedTitle": "Your rides didn't load.",
  "v2.dashboard.fromOffice": "From the office",
  "v2.dashboard.fromOfficeWhy": "This ride starts at the office, where you already are: no need to get anywhere to join it.",
  "v2.dashboard.noAccessDriver": "You're the driver: you start where the ride starts, so there's no way to get there to show.",
  "v2.dashboard.noAccessOffice": "This ride starts at the office, where you already are.",
  // The words behind the "🚶 46 min · Ⓜ+🚆 35 min" chip: {by} = "by train + metro".
  "v2.dashboard.accessTipWalk": "On foot to the pickup point: {mins}",
  "v2.dashboard.accessTipTransit": "Or {by}: {mins}, {changes}",
  // A suggestion's second line, after the icons: "35 min by metro".
  "v2.dashboard.transitShort": "{mins} {by}",
  "v2.dashboard.lockedWhy": "This ride has closed, so it takes no more requests.",
  "v2.dashboard.noRidesDayHint": "See all rides or pick another day.",
  "v2.dashboard.noRidesWeekTitle": "No rides for the next few days",
  "v2.dashboard.noRidesWeekHint": "As soon as a colleague posts a ride, you'll see it here. Rides also lists the days after these.",
  "v2.dashboard.accessBoth": "To the pickup point: {walk} on foot or {transit} {by}",
  "v2.dashboard.colleaguesPosted": {
    one: "1 colleague posted a ride to Marousi",
    other: "{n} colleagues posted rides to Marousi",
  },
  "v2.dashboard.colleaguesPostedShort": { one: "1 colleague posted a ride", other: "{n} colleagues posted rides" },
  "v2.dashboard.moreRides": { one: "+1 more ride", other: "+{count} more rides" },
  "v2.dashboard.leaves": "leaves {when}",
  "v2.dashboard.fastestRoute": "Fastest route",
  "v2.dashboard.onFoot": "on foot",
  "v2.dashboard.walkShort": "{mins} walk",
  "v2.dashboard.details": "Details",
  "v2.install.title": "T-Ride on your home screen",
  "v2.install.body": "Opens from your home screen with one tap, full screen, like any other app.",
  "v2.install.install": "Add it to your home screen",
  "v2.install.menu1": "Open your browser's menu",
  "v2.install.menu2": "Choose “Install” or “Add to Home screen”",
  "v2.install.ios1": "Tap Share in Safari's bar",
  "v2.install.ios1other": "Open your browser's menu and tap Share",
  "v2.install.ios2": "Choose “Add to Home Screen”",
  "v2.install.close": "Not now",
  "v2.install.later": "You'll always find this in your Profile.",
  "v2.install.show": "Show me how",
  "v2.install.hide": "Close",
  "v2.install.already": "T-Ride is already on your home screen — open it from there.",
  "v2.dashboard.map": "Map",
  "v2.dashboard.callDriver": "Call {name}",
  "v2.dashboard.youDrive": "You're driving",
  "v2.dashboard.seatsBooked": { one: "{booked} of 1 seat booked", other: "{booked} of {n} seats booked" },
  "v2.dashboard.shareShort": "share",
  "v2.dashboard.suggestionsTitle": "Rides that suit you",
  "v2.dashboard.suggestionsShort": "Rides that suit you",
  "v2.dashboard.seeAll": "See all",
  "v2.dashboard.dayPicker": "Ride day",
  "v2.dashboard.dayNoRides": "{day} · no rides you can ask to join",
  "v2.dashboard.closest": "nearest to you first",
  "v2.dashboard.soonest": "in order of leaving",
  "v2.dashboard.ridesCount": { one: "1 ride", other: "{n} rides" },
  "v2.dashboard.noRidesDay": "No rides on {day} yet.",
  "v2.dashboard.request": "Request a seat",
  "v2.dashboard.requestShort": "Request",
  "v2.dashboard.requestFor": "Request a seat · driver: {name}",
  "v2.dashboard.join.title": "Request a seat?",
  "v2.dashboard.join.body": "We'll send your request for the {when} ride. Driver: {name}.",
  "v2.dashboard.join.pickupHome": "Pickup near the address in your profile.",
  "v2.dashboard.join.pickupNone": "You haven't set an address, so the pickup will be at the driver's starting point.",
  "v2.dashboard.join.send": "Send request",
  "v2.dashboard.locked": "This ride is closed — no more changes can be made.",
  "v2.dashboard.lockedBadge": "Closed",
  "v2.dashboard.points.title": "Your points",
  "v2.dashboard.streakWeeks": { one: "1 week with a ride", other: "{count} weeks in a row" },
  "v2.dashboard.rankOverall": "{rank} place all time",
  "v2.dashboard.ringTo": "to {reward}",
  "v2.dashboard.ringLabel": "{percent}% of the way to {reward}: you have {have} of {need} points, {left} to go",
  "v2.dashboard.nextReward": "Next reward:",
  "v2.dashboard.pointsToGo": { one: "{count} point to go", other: "{count} points to go" },
  "v2.dashboard.toReward": { one: "1 point to go for {reward}", other: "{count} points to go for {reward}" },
  "v2.dashboard.rewardProgress": { one: "You have {have} of the {need} points — 1 to go.", other: "You have {have} of the {need} points — {left} to go." },
  "v2.dashboard.rewardProgressSince": { one: "Since you last earned it you have {have} of the {need} points — 1 to go.", other: "Since you last earned it you have {have} of the {need} points — {left} to go." },
  "v2.dashboard.rewardEvery": { one: "Every point you collect earns you this reward.", other: "Every time you collect {count} points, you earn this reward." },
  "v2.dashboard.rewardOnce": { one: "You earn it once, as soon as you collect your first point.", other: "You earn this reward once, when your total reaches {count} points." },
  "v2.dashboard.stat.saved": "saved",
  "v2.dashboard.stat.co2": "less CO₂",
  "v2.dashboard.stat.rides": "rides",
  "v2.dashboard.board.title": "Leaderboard",
  "v2.dashboard.board.week": "Week",
  "v2.dashboard.board.month": "Month",
  "v2.dashboard.board.all": "All",
  "v2.dashboard.board.timeframe": "Leaderboard period",
  "v2.dashboard.board.titleWeek": "Leaderboard · this week",
  "v2.dashboard.board.titleMonth": "Leaderboard · this month",
  "v2.dashboard.board.titleAll": "Leaderboard · all time",
  "v2.dashboard.board.allShort": "All",
  "v2.dashboard.board.seeAll": "See the full leaderboard",
  "v2.dashboard.board.you": "You · {name}",
  "v2.dashboard.board.empty": "Nobody has earned points in this period yet.",
  // ── v2.rides ── (keys for RidesV2 and OfferRideV2 go directly below)
  "v2.rides.subtitle": "Find a colleague going to work at your time and ask for a seat.",
  "v2.rides.offerSubtitle": "Post your ride. You choose who comes along.",
  "v2.rides.transitChip": "{mins} {by}",
  "v2.rides.transitTipTitle": "{mins} {by} to where the driver picks you up",
  "v2.rides.transitTipWalk": "On foot it takes {mins}.",
  "v2.rides.map.legend": "Map legend",
  "v2.rides.map.rides": "Driver pickups",
  "v2.rides.map.office": "Office",
  "v2.rides.map.home": "Your home",
  "v2.rides.map.place": "Your chosen spot",
  "v2.rides.map.walkThere": "On foot to the pickup",
  "v2.rides.map.transitThere": "By public transport to the pickup",
  "v2.rides.map.wayThere": "How you get to the pickup",
  "v2.rides.map.byTransit": "{mins} by public transport",
  "v2.rides.youDriveThis": "You're driving",
  "v2.rides.youDriveAt": "You drive at {time}",
  "v2.rides.openMyDrive": "Your ride at {time}",
  "v2.rides.driving.title": "You're driving this day",
  "v2.rides.driving.body": { one: "You can ask for a seat on another ride only if it leaves at least 1 hour before or after yours.", other: "You can ask for a seat on another ride only if it leaves at least {n} hours before or after yours." },
  "v2.rides.myDriveAt": "You drive {time}",
  "v2.rides.offer": "Offer",
  "v2.rides.dayFilter": "Departure day",
  "v2.rides.count": { one: "1 ride", other: "{n} rides" },
  "v2.rides.requestSeat": "Request a seat",
  "v2.rides.requestShort": "Request",
  "v2.rides.locked": "This ride is closed — no more changes can be made.",
  "v2.rides.lockedShort": "Closed",
  "v2.rides.confirm.title": "Request a seat?",
  "v2.rides.confirm.send": "Send request",
  "v2.rides.confirm.driverDecides": "{name} will accept or decline your request, and you'll get a notification.",
  // Where the driver starts, not the rider's home — as the ride page puts it.
  "v2.rides.confirm.pickupHome": "Meeting point: {place} — a {walk} walk from your home",
  // When public transport is quicker, as the list row shows it: {by} is "by metro".
  "v2.rides.confirm.pickupHomeTransit": "Meeting point: {place} — from your home, a {walk} walk or {transit} {by}",
  "v2.rides.confirm.meetingPoint": "Meeting point: {place}",
  "v2.rides.upcoming.all": "See all ({n})",
  "v2.rides.upcoming.less": "Show fewer",
  "v2.rides.later.more": { one: "Show 1 more", other: "Show {n} more" },
  // Durations in words (views/v2/parts/duration.js).
  "v2.dur.mins": { one: "1 min", other: "{n} min" },
  "v2.dur.hours": { one: "1 hour", other: "{n} hours" },
  "v2.dur.hoursMins": "{hours} {mins}",
  // Countdown to a departure: "in 1 hour 20 min" ({when} from duration.js).
  "v2.dur.in": "in {when}",
  "v2.rides.confirm.noHome": "You have no address in your profile, so the request goes without a pickup point.",
  "v2.rides.failed.title": "The request was not sent",
  "v2.rides.offer.routeTitle": "Your route",
  "v2.rides.offer.mapEmpty": "Pick a starting point and your route appears here.",
  "v2.rides.offer.previewEmpty": "Pick a starting point to see the cost.",
  "v2.rides.offer.fuelUsed.L": "Fuel used",
  "v2.rides.offer.fuelUsed.kWh": "Electricity used",
  "v2.rides.offer.fuelRate.L": "{consumption} litres per 100 km · {price} a litre",
  "v2.rides.offer.fuelRate.kWh": "{consumption} kWh per 100 km · {price} a kWh",
  "v2.rides.offer.startLabel": "Where you start",
  "v2.rides.offer.addCarButton": "Add your car first",
  "v2.rides.offer.routeCount": { one: "1 route", other: "{n} routes" },
  "v2.rides.offer.withTraffic": "with traffic at {time}",
  "v2.rides.offer.departurePast": "That time has already passed. Pick a departure later than now.",
  "v2.rides.offer.returnBeforeDeparture": "Your return is before you set off ({time}). Pick a return time after that.",
  "v2.rides.offer.fixTimeButton": "Fix the time first",
  "v2.rides.offer.addCarHint.L": "Add in {profile} how many litres your car uses and what you pay per litre, and this fills itself in.",
  "v2.rides.offer.addCarHint.kWh": "Add in {profile} how many kWh your car uses and what you pay per kWh, and this fills itself in.",
  "v2.rides.offer.addCarHint.any": "Add in {profile} what car you drive, what it uses and what you pay per litre or kWh, and this fills itself in.",
  "v2.rides.searchPlaceholder": "Search another address",
  "v2.rides.search.noMatch": "We couldn't find that address. Try a street and number, e.g. \"Kifisias 99\".",
  "v2.rides.pickup.search": "Starting address",
  "v2.rides.pickup.change": "Change address",
  "v2.rides.seatsFree": { one: "1 seat free", other: "{n} seats free" },
  "v2.rides.perRider": "per rider",
  "v2.rides.upDriving": { one: "Driving · {booked} of 1 seat booked", other: "Driving · {booked} of {n} seats booked" },
  "v2.rides.walkTip": "How long you walk to the spot where the driver picks you up.",
  "v2.rides.walkFar": "{mins} walk (far)",
  "v2.rides.walkFarTip": "That's a long walk. See if public transport suits you, or ask the driver whether they can pick you up closer.",
  "v2.rides.transit": "{mins} by public transport",
  "v2.rides.transitTip": "By bus, tram or metro to the spot where the driver picks you up, quicker than walking. Lines: {lines}",
  "v2.rides.transitTipPlain": "By bus, tram or metro to the spot where the driver picks you up, quicker than walking.",
  "v2.rides.days": { one: "1 day", other: "{n} days" },
  "v2.rides.why.distance": "{km} from home · leaves in {when}",
  "v2.rides.why.leaves": "Leaves in {when}",
  "v2.rides.dayPick": "Day",
  "v2.rides.dayLabel": "{day} · {count}",
  "v2.rides.dayNone": "{day} · no rides",
  "v2.rides.dayTitle.today": "Today · {day}",
  "v2.rides.dayTitle.tomorrow": "Tomorrow · {day}",
  "v2.rides.empty.day": "No rides for {day} yet.",
  "v2.rides.empty.dayFull": "Every ride for {day} is full.",
  "v2.rides.empty.dayFullHint": "You can still see them, or pick another day.",
  "v2.rides.empty.dayFree": "No free rides for {day}.",
  "v2.rides.empty.dayFreeHint": "There are rides with a share to pay. See them all, or pick another day.",
  "v2.rides.empty.showAll": "Show all",
  "v2.rides.empty.showFull": { one: "Show the full ride", other: "Show the {n} full rides" },
  "v2.rides.empty.nextDay": "Next: {day}",
  "v2.rides.onMap": "Show the ride on the map: {name}",
  "v2.rides.transitCombo": "{modes} · {mins}",
  "v2.rides.transitTipChanges": { one: "You change once.", other: "You change {n} times." },
  "v2.rides.transitTipDirect": "No changes on the way.",
  "v2.rides.offer.cost":"Cost",
  // ── v2.detail ── (keys for RideDetailV2 and PastRidesV2 go directly below)
  "v2.detail.self.label": "You are here — only you can see this dot",
  "v2.detail.self.notNow": "Not now",
  "v2.detail.self.lostTitle": "Lost your location signal",
  "v2.detail.self.private": "Only you can see your position on this map.",
  "v2.detail.access.choose": "Way to the pickup point",
  "v2.detail.access.sub": "From your home to where the driver picks you up. Pick a way to see it on the map.",
  "v2.detail.access.leaveAt": "Leave home at {time}",
  "v2.detail.access.spare": { one: "You get there {n} minute before the driver.", other: "You get there {n} minutes before the driver." },
  "v2.detail.access.walkDist": "You walk {km} to the pickup.",
  "v2.detail.access.leaveTile": "Leave home",
  "v2.detail.edit.route.trafficAt": "Times include the traffic expected at {time}.",
  "v2.detail.edit.route.arrive": "arrive {time}",
  "v2.detail.edit.route.fastest": "Fastest",
  "v2.detail.edit.route.shortest": "Shortest",
  "v2.detail.edit.route.cheapest": "Cheapest",
  "v2.detail.edit.route.each": "about, each",
  "v2.detail.edit.route.eachFull": { one: "each, if the 1 seat fills", other: "each, if all {n} seats fill" },
  "v2.detail.edit.seatShare": { one: "If the seat fills, each person pays about {amount}.", other: "If all {n} seats fill, each person pays about {amount}." },
  "v2.detail.edit.seatShareFree": "This ride is free — riders pay nothing.",
  "v2.detail.noBookings": "Nobody has asked for a seat yet. When someone does, you'll get a notification and decide here.",
  "v2.detail.noBookingsClosed": "Nobody asked for a seat before the ride closed.",
  "v2.detail.noSeatsYet": "Nobody has a seat yet.",
  "v2.detail.noSeatsClosed": "Nobody had a seat on this ride.",
  "v2.detail.seatsTakenOf": "Seats taken: {n} of {total}.",
  "v2.detail.closedBadge": "Closed",
  "v2.detail.nobodyJoined": "Nobody joined this ride.",
  "v2.detail.aloneDriver": "Nobody has joined yet, so the whole cost (€{total}) is yours. If one colleague joins, you each pay €{each}.",
  "v2.detail.eyebrowDriverCancelled": "The ride you offered",
  "v2.detail.cancelledShare": "This ride was cancelled, so nobody pays anything.",
  "v2.detail.cancelledDriver": "You cancelled this ride. Everyone who had a seat was told, and nothing more happens on it.",
  "v2.detail.cancelledRider": "The driver cancelled this ride, so you can no longer go with it.",
  "v2.detail.notFoundBody": "It may have been cancelled, or the link may be out of date. Have a look at the rides on offer now.",
  "v2.detail.noBookingsDeparted":"Nobody asked for a seat before you left.",
  "v2.detail.noRidersCancelled":"This ride was cancelled, so it has no riders.",
  "v2.detail.aloneRider":"€{total} in total. If you join, it is split two ways: €{each} each.",
  "v2.detail.freeDriver": "Free — you cover the cost.",
  "v2.detail.freeRider": "Free — the driver covers the cost.",
  "v2.detail.costEach": "Cost per person",
  "v2.detail.completeAlone": "You had no riders, so there's no cost to split. If you made the trip, tap \"Complete\" to keep it in your history.",
  "v2.detail.completeAloneBody": "You had no riders, so no cost is split. The ride goes into your history. This can't be undone.",
  "v2.detail.tooCloseToEdit": "No more changes, as departure is close. If something has changed, call your passengers.",
  "v2.detail.completeLater": "When you arrive, tap \"Complete\" here. That splits the cost and gives everyone their points.",
  "v2.detail.completeSoon": "The \"Complete\" button appears once the ride has left. Tap it when you arrive, so the cost is split and everyone gets their points.",
  "v2.detail.title": "Ride details",
  "v2.detail.eyebrowDriver": "You're driving",
  "v2.detail.eyebrowRider": "Your seat",
  "v2.detail.eyebrowOther": "A colleague's ride",
  "v2.detail.arrival": "arrives ~{time}",
  "v2.detail.leaves": "leaves {when}",
  "v2.detail.route": "Drive",
  "v2.detail.withTraffic": "with traffic at departure time",
  "v2.detail.estimated": "estimate",
  "v2.detail.seatsTaken": "seats taken",
  "v2.detail.requests": "Requests",
  "v2.detail.aboard": "In the car",
  "v2.detail.removeReturn.action": "Remove trip home",
  "v2.detail.removeReturn.title": "Remove the trip home?",
  "v2.detail.removeReturn.body": "The trip home is a separate ride. Anyone with a seat on it is notified and will need another way back. This can't be undone.",
  "v2.detail.live.title": "Live location",
  "v2.detail.live.updated": "Last updated at {time}",
  "v2.detail.live.idleDriver": "Share your location when you set off, so riders can see where you are.",
  "v2.detail.eyebrowDriverPast": "You drove",
  "v2.detail.eyebrowRiderPast": "You were a passenger",
  "v2.detail.seatsBooked": { one: "{booked} of 1 seat booked", other: "{booked} of {n} seats booked" },
  "v2.detail.splitWays": "€{total} in total, split between {n} people",
  "v2.detail.return.driverTitle": "Your trip home is a separate ride",
  "v2.detail.return.driverSub": "Leaves {time}",
  "v2.detail.return.riderSub": "Leaves {time} — ask for a seat on it separately",
  "v2.detail.clash.pendingTitle": "You've already asked for a seat that day",
  "v2.detail.clash.pendingBody": "You've already asked for a seat on {name}'s ride at {time}. If you'd rather take this one, cancel that request first.",
  "v2.detail.clash.confirmedBody": "You already have a seat at {time}, driver: {name}. If you prefer this ride, leave that one first so your seat there goes to a colleague.",
  "v2.detail.clash.drivingTitle": "You have your own ride then",
  "v2.detail.clash.drivingBody": "You're driving around then (you leave at {time}). If you can't drive after all, cancel your own ride first.",
  "v2.detail.clash.openMyDrive": "See your ride",
  "v2.detail.cancelRide.keep": "No, keep the ride",
  "v2.detail.cancelRide.alsoReturn": "Also cancel the trip home ({when} · {riders})",
  "v2.detail.cancelRide.noReturnRiders": "no passengers",
  "v2.detail.cancelRide.alsoReturnHint": "The trip home is a separate ride. Leave this off and the trip home stays as it is.",
  "v2.detail.removeReturn.keep": "No, keep the trip home",
  "v2.pastRides.subtitle": "The rides you've completed, as driver or passenger, and the points they earned you.",
  "v2.pastRides.role.all": "All",
  "v2.pastRides.role.drove": "As driver",
  "v2.pastRides.role.rode": "As passenger",
  "v2.pastRides.badge.rode": "RODE",
  "v2.pastRides.pointsUnit": { one: "point", other: "points" },
  "v2.pastRides.stat.points": "Points you earned",
  "v2.pastRides.pointsPast": "Older rides keep the points that applied at the time.",
  "v2.pastRides.office": "Office",
  "v2.pastRides.pointsFailed": "We couldn't load your points just now. All your rides are here; refresh the page in a little while to see the points too.",
  "v2.pastRides.period.7": "Your rides in the last 7 days",
  "v2.pastRides.period.30": "Your rides in the last 30 days",
  "v2.pastRides.period.all": "All your rides",
  "v2.pastRides.empty.hint": "Try “All time” and “All”, or show everything with one tap.",
  "v2.pastRides.empty.reset": "Show all",
  "v2.pastRides.none.title": "You haven't shared a ride yet",
  "v2.pastRides.none.hint": "Once your first ride is done, as driver or passenger, you'll find it here.",
  // ── v2.leaders ── (keys for LeaderboardV2 and NotificationsV2 go directly below)
  "v2.notif.completedWith": { one: "You completed the ride with 1 passenger", other: "You completed the ride with {n} passengers" },
  "v2.leaders.subtitle": "See who has collected the most points and how close you are to your next reward.",
  "v2.leaders.emptyAction": "Find a ride",
  "v2.notif.subtitle": "See who asked for a seat, what drivers answered and what changed on your rides.",
  "v2.notif.emptyHint": "When you ask for a seat, or someone asks you, the answer comes here.",
  // The feed could not be loaded: what to do about it, under notif.loadFailed.
  "v2.notif.loadFailedHint": "Check that you are online and press “Try again”. If it still fails, try again in a few minutes.",
  "v2.leaders.timeframe": "Ranking period",
  "v2.leaders.yourPosition": "Your position",
  "v2.leaders.offBoard": "The board shows fewer places than yours — your position still counts.",
  "v2.leaders.you": "You",
  "v2.leaders.youRow": "You · {name}",
  "v2.leaders.rest": "The rest of the board",
  "v2.leaders.nextReward": "Coming up first:",
  "v2.leaders.unread": "New",
  "v2.leaders.topSpot.week": "You're #1 this week — hold on to your spot.",
  "v2.leaders.topSpot.month": "You're #1 this month.",
  "v2.leaders.topSpot.all": "You're #1 overall.",
  "v2.leaders.overtake": { one: "{points} to pass {name} — {rides}.", other: "{points} to pass {name} — {rides}." },
  "v2.leaders.overtakeRides": { one: "about one more shared ride", other: "about {count} more shared rides" },
  "v2.leaders.podiumPoints": { one: "{points} point", other: "{points} points" },
  // Both placeholders are consumed by a split in LeaderboardV2; {points} is v2.leaders.behindPoints.
  "v2.leaders.behind": { one: "You're {points} ahead of {name}, right behind you.", other: "You're {points} ahead of {name}, right behind you." },
  "v2.leaders.behindPoints": { one: "{points} point", other: "{points} points" },
  "v2.notif.groupCount": { one: "1 notification", other: "{n} notifications" },
  "v2.notif.freeForRiders": "Free for riders",
  "v2.leaders.notif.closed": "CLOSED",
  // A join request once it is no longer waiting: said in the past, so it never reads as a new ask.
  "v2.leaders.notif.requestAccepted": "You accepted {name} on your ride",
  "v2.leaders.notif.requestDeclined": "You turned down {name}'s request to join your ride",
  "v2.leaders.notif.requestAsked": "{name} asked to join your ride",
  "v2.leaders.notif.bookingCompleted": "Your ride with {name} is complete",
  "v2.leaders.notif.rideCompleted": "You drove {name}",
  // A departure reminder viewed after the ride's departure time.
  "v2.leaders.notif.reminderPast": "We reminded you your ride was about to leave",
  "v2.leaders.notif.reminderBadge": "REMINDER",
  "v2.leaders.rewards.title": "Points and rewards",
  "v2.leaders.rewards.nextGoal": "Your next goal",
  "v2.leaders.rewards.progress": { one: "{have} / {target} point", other: "{have} / {target} points" },
  "v2.leaders.rewards.toGo": { one: "{points} point to go", other: "{points} points to go" },
  "v2.leaders.rewards.inPoints": { one: "in {points} point", other: "in {points} points" },
  "v2.leaders.rewards.goalsReached": { one: "1 goal reached", other: "{n} goals reached" },
  "v2.leaders.rewards.allCollected": { one: "1 reward you've had as often as it's given", other: "{n} rewards you've had as often as they're given" },
  "v2.leaders.rewards.earnedTimes": { one: "earned once so far", other: "earned {n} times so far" },
  "v2.leaders.rewards.show": "Show",
  "v2.leaders.rewards.hide": "Hide",
  "v2.leaders.rewards.all": "All rewards",
  "v2.leaders.rewards.fewer": "Hide the rewards",
  "v2.leaders.rewards.laterGoals": "Further goals",
  "v2.leaders.rewards.showAllGoals": "Show all ({n})",
  "v2.leaders.rewards.showFewerGoals": "Show fewer",
  "v2.leaders.rewards.every": { one: "every {points} point", other: "every {points} points" },
  "v2.leaders.rewards.cap": { one: "once only", other: "up to {n} times" },
  // Rewards card sections fold under their eyebrow; a folded section keeps a one-line summary.
  "v2.leaders.rewards.closeAll": "Close all",
  "v2.leaders.rewards.openAll": "Open all",
  "v2.leaders.rewards.earnedTitle": "Goals you've reached",
  "v2.leaders.rewards.goalsCount": { one: "1 goal", other: "{n} goals" },
  "v2.leaders.rewards.rewardsCount": { one: "1 reward", other: "{n} rewards" },
  "v2.leaders.rewards.nearestGoal": { one: "nearest in {points} point", other: "nearest in {points} points" },
  "v2.leaders.rewards.nearestReward": { one: "nearest in {points} point", other: "nearest in {points} points" },
  "v2.leaders.rewards.allTaken": "all collected",
  "v2.leaders.rewards.times": { one: "earned once", other: "earned {n} times" },
  "v2.leaders.rewards.timesOf": { one: "earned {got} of {n} time", other: "earned {got} of {n} times" },
  // ── v2.profile ── (keys for ProfileV2, PointsSystemV2, NotFoundV2 go directly below)
  "v2.profile.subtitle": "Set where you start from, your phone and car, and how we should notify you.",
  "v2.profile.delete.passwordHint": "Type your password, then press “{action}”.",
  "v2.profile.pickupTitle": "Usual pickup point",
  "v2.profile.uiVersion.switched": "Everyone now sees {version}.",
  "v2.profile.uiVersion.confirmTitle": "Change the look for everyone?",
  "v2.profile.uiVersion.confirmBody": "All colleagues will see {version} as soon as they open or refresh the app, and the sign-in page changes too. You can change it back here whenever you like.",
  "v2.profile.uiVersion.confirmKeep": "No, keep {version}",
  "v2.profile.uiVersion.confirmAction": "Switch to {version}",
  "v2.profile.seeAll": "See all",
  "v2.profile.backHome": "Back to home",
  "v2.profile.points.unsaved": "You have unsaved changes.",
  "v2.profile.points.adminOnly": "Only administrators can change the points system.",
  "v2.profile.points.collapseAll": "Collapse all",
  "v2.profile.points.expandAll": "Expand all",
  "v2.profile.points.toggleSection": "Open or close: {title}",
  "v2.profile.points.unsavedShort": "Unsaved changes",
  "v2.profile.points.discardShort": "Discard",
  "v2.profile.points.showAll": "Show all ({n})",
  "v2.profile.points.showFewer": "Show fewer",
  // The recurring card's add pill; its spoken name is the full "admin.recurring.add".
  "v2.profile.points.addRecurring": "Add a reward",
  // The closing bar's short form of "admin.err.missing"; the fields themselves are ringed in red.
  "v2.profile.points.fixRequired": "Some required fields are empty. Fill in the ones outlined in red and press \"Save changes\" again.",
  // Profile: a refused driver switch or ride-email choice, which the shared hook put back without a word.
  "v2.profile.driver.saveFailed": "The change wasn't saved. Check your connection and flip the switch again.",
  "v2.profile.rideEmails.saveFailed": "Your email choice wasn't saved. Check your connection and choose again.",
  "v2.profile.points.nothingToSave": "There are no changes to save.",
  "v2.profile.points.count": { one: "1 reward", other: "{n} rewards" },
  "v2.profile.points.summary.points": "Passenger {rider} · driver {perRider} per passenger and {completion} per ride",
  "v2.profile.points.summary.ranking": "Week {week} days · month {month} days · {size} places listed",
  "v2.profile.points.rowOffered": "Offered: {name}",
  "v2.profile.points.rowEdit": "Edit: {name}",
  "v2.profile.points.rowClose": "Close: {name}",
  "v2.profile.points.rowRemove": "Remove: {name}",
  "v2.profile.points.copyHint": "The name and \"What they get\" show on the Leaderboard for everyone — fill them in in Greek and English.",
  "v2.profile.impact.failed": "We couldn't load your totals. Refresh the page to try again.",
  "v2.profile.uiVersion.hint": "Applies to every colleague and the sign-in page. They see it as soon as they open or refresh the app.",
  "v2.profile.driver.hint":
    "You can always join rides as a passenger. Turn it on and the questions about your car appear below; they work out what each person pays towards the ride.",
  "v2.profile.car.hint": "Each person's share of the cost — yours too — is worked out from this.",
  "v2.profile.impact.saved": "You saved",
  "v2.profile.impact.co2": "Less CO₂",
  "v2.profile.impact.rides": "Rides",
  "v2.profile.fuel.COMBUSTION": "Fuel",
  "v2.profile.fuel.ELECTRIC": "Electric",
  "v2.profile.fuel.covers": "“Fuel” means petrol, diesel, LPG or a hybrid you don't plug in. “Electric” means a car you charge.",
  "v2.profile.streakChip": { one: "1 week in a row", other: "{n} weeks in a row" },
  "v2.profile.streakFull": { one: "1 week with a shared ride", other: "{n} weeks in a row with a shared ride" },
  // Profile photo (identity card, framing dialog, removal).
  "v2.profile.photo.add": "Add a photo",
  "v2.profile.photo.change": "Change photo",
  "v2.profile.photo.remove": "Remove",
  "v2.profile.photo.removeLong": "Remove photo",
  "v2.profile.photo.cameraLabel": "Change your photo",
  "v2.profile.photo.tipLabel": "Who sees your photo?",
  "v2.profile.photo.title": "Your photo",
  "v2.profile.photo.explain": "Only colleagues who are signed in can see it. We remove location details from the photo.",
  "v2.profile.photo.formats": "Pick a photo from your phone or computer (JPG or PNG, up to {mb} MB).",
  "v2.profile.danger.title": "Delete account",
  "v2.profile.pickup.clearSearch": "Clear the search",
  "v2.profile.pickup.searchPlaceholder": "Type an address",
  "v2.profile.photo.dragHint": "Drag the photo and change the zoom so you're inside the circle.",
  "v2.profile.photo.cropLabel": "Your photo. Arrow keys move it; + and − change the zoom.",
  "v2.profile.photo.zoom": "Zoom",
  "v2.profile.photo.zoomIn": "Zoom in",
  "v2.profile.photo.zoomOut": "Zoom out",
  "v2.profile.photo.progressLabel": "Upload progress",
  "v2.profile.photo.uploading": "Sending your photo… {pct}%",
  "v2.profile.photo.finishing": "Almost done…",
  "v2.profile.photo.saved": "Your photo is saved.",
  "v2.profile.photo.removed": "Your photo was removed.",
  "v2.profile.photo.removeTitle": "Remove your photo?",
  "v2.profile.photo.removeBody": "Your initials will show instead.",
  "v2.profile.photo.dropHere": "Drop the photo here",
  // ── v2.public ── (keys for LoginV2, ForgotPasswordV2, 404 go directly below)
  "v2.public.forgot": "Forgot your password?",
  "v2.public.stepEmail": "Email",
  "v2.public.stepCode": "Code",
  "v2.public.codeIncomplete": "Enter all 6 digits to continue.",
  "v2.public.demoTitle": "TEST ACCOUNTS",
  "v2.public.stepOf": "Step {n} of {total}",
  "v2.public.strength": "Password strength: {label}",
  "v2.public.resent": "We've sent you a new code. The old one no longer works.",
  "v2.public.welcomeTitle": "Welcome to T-Ride",
  "v2.public.welcomeHint": "Sign in with your work email to share your commute to Marousi.",
  "v2.public.blurb": "T-Ride is running as a trial for now. There's no sign-up: your account is set up for you by the app's administrator.",
  "v2.public.asideTitle": "Get to the office together with colleagues",
  "v2.public.reset.asideEyebrow": "NEW PASSWORD",
  "v2.public.reset.asideTitle": "Set a new password in three steps",
  "v2.public.reset.aside1": "Enter your work email.",
  "v2.public.reset.aside2": "Type the 6-digit code we email you.",
  "v2.public.reset.aside3": "Choose a new password and sign in again.",
  "v2.public.reset.asideFooter": "Can't find the email? Check your spam folder too, or ask for a new code.",
  "v2.public.err.emailEmpty": "Enter your work email.",
  "v2.public.err.emailFormat": "That doesn't look like an email address. Write it in full, e.g. name@ote.gr.",
  "v2.public.err.passwordEmpty": "Enter your password.",
  "v2.public.pointsEyebrow": "WHY T-RIDE",
  "v2.public.point.1": "Find colleagues who make the same trip as you and go to work together.",
  "v2.public.point.2": "Every ride you share earns you points, and points bring rewards.",
  "v2.public.point.3": "Fewer cars on the road means less traffic and less pollution in Marousi.",
};
